import _ from "lodash";
import client from "lib/apolloClient/client";
import update from "immutability-helper";
import axios from "axios";

import {
  getAIConversationsFromCache,
  writeAIConversationsInCache,
} from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/LessonPlanningGraphqlHelpers";

import {
  createAttachmentsMutation,
  deleteAttachmentMutation,
  updateAttachmentMutation,
} from "modules/CommonMutations";
import { updateAIMessageMutation } from "UIComponents/AIChat/modules/AIChatMutations";

// modules
import { getUserId } from "modules/CommonSelectors";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { generateRandomId } from "services/generateRandomId";

// constants
import { TEMP_ID } from "./Constants";

export const deleteAIConversationAttachment = ({ input, aiConversationId }) => {
  return async (dispatch, getState) => {
    const conversationData = getAIConversationsFromCache({
      id: aiConversationId,
    });

    try {
      const { attachmentIds } = input || {};

      const updatedConversationData = {
        ...(conversationData || {}),
        attachments: _.filter(
          conversationData?.attachments,
          attachment => !_.includes(attachmentIds, _.get(attachment, "id"))
        ),
        __typename: "AiConversation",
      };

      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: updatedConversationData,
        },
      });

      return await client.mutate({
        mutation: deleteAttachmentMutation,
        variables: { attachmentIds },
      });
    } catch (err) {
      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: conversationData,
        },
      });
    }
  };
};

export const pushNewAttachmentToCache = params => {
  const { aiConversationId, attachments } = params;

  const conversationData = getAIConversationsFromCache({
    id: aiConversationId,
  });

  let updatedConversationData = conversationData;

  const conversationAttachments = _.get(conversationData, "attachments", []);

  if (_.some(conversationAttachments, ({ id }) => _.includes(id, TEMP_ID))) {
    const filteredAttachments = _.filter(
      conversationAttachments,
      ({ id }) => !_.includes(id, TEMP_ID)
    );

    const newAttachments = [...filteredAttachments, ...attachments];

    updatedConversationData = update(conversationData, {
      attachments: {
        $set: newAttachments,
      },
    });
  } else {
    updatedConversationData = update(conversationData, {
      attachments: {
        $push: attachments,
      },
    });
  }

  writeAIConversationsInCache({
    variables: {
      id: aiConversationId,
    },
    data: {
      node: updatedConversationData,
    },
  });
};

const extractTitleFromText = text => {
  const titleRegex = /Video Title:\s*(.+)/i;
  const match = text.match(titleRegex);
  return match ? match[1].trim() : null;
};

export const updateAIConversationAttachment = ({ input, aiConversationId }) => {
  return async (dispatch, getState) => {
    const conversationData = getAIConversationsFromCache({
      id: aiConversationId,
    });

    try {
      const { id: attachmentId, ...rest } = input || {};

      const updatedConversationData = {
        ...conversationData,
        attachments: _.map(conversationData?.attachments, attachment => {
          if (attachment.id === attachmentId) {
            return { ...attachment, ...rest };
          }

          return attachment;
        }),
        __typename: "AiConversation",
      };

      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: updatedConversationData,
        },
      });

      return await client.mutate({
        mutation: updateAttachmentMutation,
        variables: { input },
      });
    } catch (e) {
      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: conversationData,
        },
      });
    }
  };
};

export const createAIConversationAttachments = params => {
  const { input, aiConversationId, updateTitleAfterCreation = false } = params;

  return async (dispatch, getState) => {
    const conversationData = getAIConversationsFromCache({
      id: aiConversationId,
    });

    try {
      const userId = getUserId(getState());

      const attachments = _.map(input, attachment => {
        return {
          id: `${TEMP_ID}${generateRandomId()}`,
          ...attachment,
          name: !_.isEmpty(attachment?.name) ? attachment?.name : "Untitled",
          thumbUrl: null,
          title: null,
          streamUrl: null,
          signedUrl: null,
          parentType: "AI_CONVERSATION",
          metadata: null,
          isRead: true,
          similarityReport: null,
          lockingState: null,
          questionAttachment: null,
          createdBy: {
            id: userId,
            type: "staff",
            __typename: "Staff",
          },
          __typename: "Attachment",
        };
      });

      pushNewAttachmentToCache({
        aiConversationId,
        attachments,
      });

      const result = await client.mutate({
        mutation: createAttachmentsMutation,
        variables: { input },
        update: (
          cache,
          {
            data: {
              platform: { createAttachment },
            },
          }
        ) => {
          pushNewAttachmentToCache({
            aiConversationId,
            attachments: createAttachment,
          });
        },
      });

      const createdAttachments = _.get(
        result,
        "data.platform.createAttachment",
        []
      );

      // When url is from YouTube, the scraped name is "- YouTube",
      //  so we are getting the original video title and updating it
      if (updateTitleAfterCreation) {
        const attachment = _.get(createdAttachments, "0");
        const { type, name, metadata } = attachment || {};

        if (type === "LINK" && name === "- YouTube") {
          const extractedContentDetails = _.get(
            metadata,
            "extractedContentDetails"
          );
          const url = _.get(extractedContentDetails, "url");

          if (url) {
            try {
              // Fetch the data from the URL
              const response = await axios.get(url);
              const fetchedText = response.data;

              const title = extractTitleFromText(fetchedText);

              if (title) {
                // Prepare input for the update mutation
                const updateInput = {
                  id: attachment.id,
                  name: title,
                };

                await dispatch(
                  updateAIConversationAttachment({
                    input: updateInput,
                    aiConversationId,
                  })
                );

                // udpate the title for the created attachment
                const updatedAttachments = update(createdAttachments, {
                  [0]: {
                    name: { $set: title },
                  },
                });

                return updatedAttachments;
              }
            } catch (err) {}
          }
        }
      }

      return createdAttachments;
    } catch {
      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: conversationData,
        },
      });
    }
  };
};

export const updateAIMessage = ({ variables }) => {
  return async dispatch => {
    try {
      return await client.mutate({
        mutation: updateAIMessageMutation,
        variables,
      });
    } catch {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};
